import { useState, useCallback } from 'react';
import { useApi } from '@/hooks/useApi';
import { useApiQuery } from '@/hooks/useApiQuery';

interface MasterRecord {
  id: number;
  name: string;
  description: string | null;
  is_active: number;
  format_hint?: string | null;
}

interface MasterDataPanelProps {
  title: string;
  subtitle: string;
  icon: string;
  endpoint: string;
  showFormatHint?: boolean;
}

export default function MasterDataPanel({ title, subtitle, icon, endpoint, showFormatHint }: MasterDataPanelProps) {
  const { get, post, put, del } = useApi();
  const { data: records, loading, refetch } = useApiQuery<MasterRecord[]>({ endpoint });

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [formatHint, setFormatHint] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<MasterRecord | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const resetForm = () => {
    setName('');
    setDescription('');
    setFormatHint('');
    setEditingId(null);
    setError('');
  };

  const openAdd = () => {
    resetForm();
    setIsAddOpen(true);
  };

  const openEdit = useCallback((record: MasterRecord) => {
    setEditingId(record.id);
    setName(record.name);
    setDescription(record.description || '');
    setFormatHint(record.format_hint || '');
    setError('');
    setIsAddOpen(true);
  }, []);

  const closeModal = () => {
    setIsAddOpen(false);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const payload: Record<string, unknown> = {
        name: name.trim(),
        description: description.trim() || null,
      };
      if (showFormatHint) {
        payload.formatHint = formatHint.trim() || null;
      }

      if (editingId) {
        await put(`${endpoint}/${editingId}`, payload);
      } else {
        await post(endpoint, payload);
      }
      closeModal();
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await del(`${endpoint}/${deleteTarget.id}`);
      setDeleteTarget(null);
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-100 p-6">
      <div className="flex items-start sm:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center shrink-0">
            <i className={`${icon} text-brand-600 text-lg`} />
          </div>
          <div>
            <h3 className="font-bold text-stone-900 text-base">{title}</h3>
            <p className="text-stone-400 text-xs mt-0.5">{subtitle}</p>
          </div>
        </div>
        <button
          onClick={openAdd}
          className="bg-brand-600 hover:bg-brand-700 text-white font-semibold px-4 py-2 rounded-full text-xs transition-colors flex items-center gap-1.5 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-add-line" />
          Add {title.replace('Types', '')}
        </button>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-12 bg-stone-50 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="divide-y divide-stone-50">
          {(records?.length || 0) === 0 && (
            <div className="text-center py-8 text-stone-400 text-sm flex flex-col items-center gap-2">
              <i className="ri-database-2-line text-stone-300 text-2xl" />
              No records configured yet.
            </div>
          )}
          {records?.map((r) => (
            <div
              key={r.id}
              className="flex items-center justify-between py-3 px-2 hover:bg-stone-50 rounded-lg transition-colors group"
            >
              <div className="min-w-0 flex-1">
                <p className="text-stone-900 text-sm font-medium truncate">{r.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {r.description && (
                    <p className="text-stone-400 text-xs truncate">{r.description}</p>
                  )}
                  {showFormatHint && r.format_hint && (
                    <span className="text-stone-400 text-[10px] bg-stone-50 px-1.5 py-0.5 rounded">
                      Format: {r.format_hint}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 ml-3 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => openEdit(r)}
                  className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-stone-100 text-stone-400 hover:text-stone-600 transition-colors cursor-pointer"
                  title="Edit"
                >
                  <i className="ri-pencil-line text-sm" />
                </button>
                <button
                  onClick={() => setDeleteTarget(r)}
                  className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors cursor-pointer"
                  title="Delete"
                >
                  <i className="ri-delete-bin-line text-sm" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Modal */}
      {isAddOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-stone-900/30 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h4 className="text-lg font-bold text-stone-900">
                  {editingId ? 'Edit' : 'Add'} {title.replace('Types', '')}
                </h4>
                <p className="text-stone-400 text-xs">
                  {editingId ? 'Update the details below.' : 'Define a new record.'}
                </p>
              </div>
              <button
                onClick={closeModal}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors cursor-pointer"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-stone-700 text-sm font-medium mb-1.5">Name *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Private Limited"
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                />
              </div>
              <div>
                <label className="block text-stone-700 text-sm font-medium mb-1.5">Description</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Optional description"
                  className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                />
              </div>
              {showFormatHint && (
                <div>
                  <label className="block text-stone-700 text-sm font-medium mb-1.5">Format Hint</label>
                  <input
                    type="text"
                    value={formatHint}
                    onChange={(e) => setFormatHint(e.target.value)}
                    placeholder="e.g., 12-digit number"
                    className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400"
                  />
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-red-600 text-xs flex items-center gap-2">
                  <i className="ri-error-warning-line" />
                  {error}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-2.5 rounded-full border border-stone-200 text-stone-600 font-medium text-sm hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-brand-600 hover:bg-brand-700 disabled:bg-brand-300 text-white font-semibold py-2.5 rounded-full text-sm transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  {submitting && <i className="ri-loader-4-line animate-spin" />}
                  {editingId ? 'Update' : 'Add'} Record
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-stone-900/30 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
                <i className="ri-delete-bin-line text-red-500 text-lg" />
              </div>
              <div>
                <h4 className="text-base font-bold text-stone-900">Delete Record</h4>
                <p className="text-stone-400 text-xs">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-stone-600 text-sm mb-6">
              Are you sure you want to delete <strong>{deleteTarget.name}</strong> from {title}?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-2.5 rounded-full border border-stone-200 text-stone-600 font-medium text-sm hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteLoading}
                className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white font-semibold py-2.5 rounded-full text-sm transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
              >
                {deleteLoading && <i className="ri-loader-4-line animate-spin" />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}